import { supabase } from '../supabaseClient.js';

/**
 * Migrate props table to use category_id instead of category string
 * This adds the new column and migrates existing data
 */
const migratePropsToCategories = async () => {
  try {
    console.log('🔄 Starting props table migration...');

    // Step 1: Check if category_id column exists, if not we'll add it during updates
    console.log('📋 Checking props table structure...');

    // Step 2: Get all existing props with their current category strings
    console.log('📊 Fetching existing props...');
    const { data: existingProps, error: propsError } = await supabase
      .from('props')
      .select('id, category, name');

    if (propsError) throw propsError;

    if (!existingProps || existingProps.length === 0) {
      console.log('✅ No existing props to migrate');
      return { success: true, migratedCount: 0 };
    }

    console.log(`📦 Found ${existingProps.length} props to analyze`);

    // Step 3: Get all categories for matching
    console.log('📂 Fetching categories for matching...');
    const { data: categories, error: categoriesError } = await supabase
      .from('categories')
      .select('id, name, level, parent_id');

    if (categoriesError) throw categoriesError;

    console.log(`📋 Found ${categories.length} categories to work with`);

    // Build materialized paths manually if they don't exist
    const buildPath = (categoryId, categoriesMap) => {
      const category = categoriesMap.get(categoryId);
      if (!category) return '';
      
      if (!category.parent_id) {
        return category.name;
      }
      
      const parentPath = buildPath(category.parent_id, categoriesMap);
      return parentPath ? `${parentPath}/${category.name}` : category.name;
    };

    // Create a map for faster lookups
    const categoriesMap = new Map();
    categories.forEach(cat => categoriesMap.set(cat.id, cat));

    // Add computed paths to categories
    categories.forEach(cat => {
      cat.computed_path = buildPath(cat.id, categoriesMap);
    });

    // Step 4: Create category mapping
    const categoryMap = new Map();
    categories.forEach(cat => {
      // Map by exact name
      categoryMap.set(cat.name.toLowerCase(), cat.id);
      
      // Map by path parts (e.g., "Furniture/Seating" -> Seating category)
      if (cat.computed_path) {
        const pathParts = cat.computed_path.split('/');
        pathParts.forEach(part => {
          if (part.trim()) {
            categoryMap.set(part.toLowerCase(), cat.id);
          }
        });
      }
    });

    // Additional manual mappings for common category strings
    const manualMappings = {
      'chairs': 'Chairs',
      'tables': 'Tables',
      'furniture': 'Furniture',
      'seating': 'Seating',
      'costume': 'Costumes & Textiles',
      'costumes': 'Costumes & Textiles',
      'clothing': 'Costumes & Textiles',
      'props': 'Hand Props',
      'lighting': 'Lighting & Effects',
      'lights': 'Lighting & Effects',
      'sound': 'Audio & Electronics',
      'audio': 'Audio & Electronics',
      'weapons': 'Weapons & Combat',
      'armor': 'Weapons & Combat',
      'vehicles': 'Vehicles & Transport',
      'set pieces': 'Set Pieces',
      'backdrop': 'Set Pieces',
      'kitchen': 'Kitchen & Dining',
      'dining': 'Kitchen & Dining',
      'books': 'Books & Papers',
      'tools': 'Tools & Instruments'
    };

    // Add manual mappings to category map
    Object.entries(manualMappings).forEach(([key, targetName]) => {
      const targetCategory = categories.find(cat => 
        cat.name.toLowerCase() === targetName.toLowerCase()
      );
      if (targetCategory) {
        categoryMap.set(key, targetCategory.id);
      }
    });

    console.log(`🗂️ Created category mappings for ${categoryMap.size} items`);
    console.log('Sample mappings:', Array.from(categoryMap.entries()).slice(0, 5));

    // Step 5: Migrate each prop
    console.log('🔄 Starting prop migration...');
    let migratedCount = 0;
    let defaultCategoryId = null;

    // Get a default category (Hand Props) for unmapped items
    const defaultCategory = categories.find(cat => cat.name === 'Hand Props');
    if (defaultCategory) {
      defaultCategoryId = defaultCategory.id;
    }

    for (const prop of existingProps) {
      try {
        let categoryId = null;

        // Try to match the existing category string
        if (prop.category && typeof prop.category === 'string') {
          const categoryKey = prop.category.toLowerCase().trim();
          categoryId = categoryMap.get(categoryKey);

          // Try partial matches
          if (!categoryId) {
            for (const [key, id] of categoryMap.entries()) {
              if (categoryKey.includes(key) || key.includes(categoryKey)) {
                categoryId = id;
                break;
              }
            }
          }
        }

        // Use default category if no match found
        if (!categoryId) {
          categoryId = defaultCategoryId;
          console.log(`⚠️  No category match for "${prop.category}" in prop "${prop.name}", using default`);
        }

        // Update the prop with category_id
        if (categoryId) {
          const { error: updateError } = await supabase
            .from('props')
            .update({ category_id: categoryId })
            .eq('id', prop.id);

          if (updateError) {
            console.error(`❌ Failed to update prop ${prop.name}:`, updateError);
          } else {
            migratedCount++;
            if (migratedCount % 10 === 0) {
              console.log(`✅ Migrated ${migratedCount}/${existingProps.length} props...`);
            }
          }
        }

      } catch (err) {
        console.error(`❌ Error migrating prop ${prop.name}:`, err);
      }
    }

    console.log('✅ Props migration completed!');
    console.log(`📊 Migration summary:`);
    console.log(`   • Total props analyzed: ${existingProps.length}`);
    console.log(`   • Successfully migrated: ${migratedCount}`);
    console.log(`   • Failed migrations: ${existingProps.length - migratedCount}`);

    return {
      success: true,
      totalProps: existingProps.length,
      migratedCount: migratedCount,
      failedCount: existingProps.length - migratedCount
    };

  } catch (error) {
    console.error('❌ Error during props migration:', error);
    throw error;
  }
};

// Export for use in other components
export default migratePropsToCategories;
